import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { UserRole } from '@mayksel/types';

interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
}

interface WorkspaceInvite {
  id: string;
  email: string;
  role: UserRole;
  expiresAt: string;
  createdAt: string;
}

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useMembers() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<WorkspaceMember[]>({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<WorkspaceMember[]>('/v1/members', opts);
    },
    enabled: !!workspaceId,
  });
}

export function usePendingInvites() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<WorkspaceInvite[]>({
    queryKey: ['invites', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<WorkspaceInvite[]>('/v1/members/invites', opts);
    },
    enabled: !!workspaceId,
  });
}

export function useSendInvite() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: UserRole }) => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.post<WorkspaceInvite>('/v1/members/invites', { email, role }, opts);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
    },
  });
}

export function useRevokeInvite() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.delete(`/v1/members/invites/${inviteId}`, opts);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
    },
  });
}

export function useUpdateMemberRole() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.patch<WorkspaceMember>(`/v1/members/${userId}/role`, { role }, opts);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
    },
  });
}

export function useRemoveMember() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await getToken();
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.delete(`/v1/members/${userId}`, opts);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
    },
  });
}
